import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  APIcreateExport,
  APIdownloadExport,
  APIgetExport,
} from '../api/Export';

import ErrorBoundary from './ErrorBoundary';
import Loading from './Loading';

import { isSafari } from '../helpers';
import {
  userIsAdmin,
  userIsManager,
} from '../helpers/Auth';

const ExportModal = ({
  handleClose,
  selectedFilters,
  collection,
}) => {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [exportId, setExportId] = useState(null);
  const [exportObject, setExportObject] = useState(null);
  const intl = useIntl();

  const loadExport = useCallback(() => {
    if (!exportId || exportObject?.path) {
      return null;
    }
    return APIgetExport(exportId).then((response) => {
      setExportObject(response);
    });
  }, [exportId, exportObject]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadExport();
    }, 2000);
    return () => clearInterval(interval);
  }, [loadExport]);

  const filters = useMemo(() => [...selectedFilters], [selectedFilters]);
  if (collection) {
    filters.push({
      type: 'collections',
      title: intl.formatMessage({ defaultMessage: 'Collection' }),
      label: collection.name,
      key: collection.uuid,
    });
  }

  useEffect(() => {
    const createExport = async () => {
      try {
        const response = await APIcreateExport({ filters });
        setExportId(response.uuid);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    createExport();
  }, [filters]);

  const handleDownload = async (e) => {
    if (isSafari()) {
      setDownloading(true);
      e.preventDefault();
      if (exportObject && exportObject.path) {
        await APIdownloadExport(exportObject.path, `${exportObject.uuid}.csv`);
      }
    }
    handleClose();
  };

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        Export
      </ModalHeader>
      <ModalBody>
        <div className="center">
          {loading && (
            <>
              <p>
                {intl.formatMessage({ defaultMessage: 'Preparing your export...' })}
              </p>
              <Loading />
            </>
          )}
          {!loading && (
            (exportObject && exportObject.path) ? (
              <>
                <p>
                  {intl.formatMessage({ defaultMessage: 'Your export is ready' })}
                </p>
                <p>
                  <Button
                    tag="a"
                    href={exportObject.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDownload}
                    download={`${exportObject.uuid}.csv`}
                    disabled={downloading}
                  >
                    {downloading ? (
                      intl.formatMessage({ defaultMessage: 'Wait...' })
                    ) : (
                      intl.formatMessage({ defaultMessage: 'Download' })
                    )}
                  </Button>
                </p>
              </>
            ) : (
              <>
                <p>
                  {intl.formatMessage({ defaultMessage: 'Your export is processing' })}<br />
                </p>
                <p>
                  {(userIsAdmin() || userIsManager()) ? (
                    intl.formatMessage({
                      defaultMessage: 'Wait here until it\'s completed or go to the <link>exports page</link> to download it later.',
                    }, {
                      link: (msg) => (
                        <Link to="/admin/settings/export" target="_blank">
                          {msg}
                        </Link>
                      ),
                    })
                  ) : (
                    intl.formatMessage({ defaultMessage: 'Wait here until it\'s completed' })
                  )}
                </p>
                {exportObject && (
                  <span className="export-progress">
                    Exported files: {exportObject.results}
                  </span>
                )}
              </>
            )
          )}
        </div>
      </ModalBody>
      <ModalFooter className="justify-r">
        <div className="buttons-group">
          <Button
            color="primary"
            disabled={loading}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Close' })}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  selectedFilters: state.Files.selectedFilters,
  filesTotal: state.Files.filesTotal,
  collection: state.Collections.collection,
});

ExportModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  collection: PropTypes.object,
};

ExportModal.defaultProps = {
  collection: null,
};

export default connect(mapStateToProps)(ExportModal);
